import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, LogIn, LogOut, ShoppingCart, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { getDashboardRoute } from '@/features/auth/utils/role-utils';
import { useCartItemCount } from '@/features/cart/stores/cart-store';
import { useSettings } from '@/features/settings/hooks/use-settings';
import { cn } from '@/lib/utils';

export function GlobalNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const itemCount = useCartItemCount();
  const { settings } = useSettings();
  const hasLogo = Boolean(settings.logo_url);

  const links = [
    { href: '/', label: 'Home' },
    { href: '/catalog', label: 'Catalog' },
    { href: '/cart', label: 'Cart' },
  ];

  return (
    <nav className="sticky top-0 z-20 border-b border-brand-100 bg-white/95 shadow-sm backdrop-blur">
      <div className="mx-auto max-w-6xl px-4 py-3">
        <div className="flex items-center justify-between gap-3 sm:gap-4">
          <Link
            to="/"
            className="group flex min-w-0 items-center gap-3 rounded-2xl px-1 py-1 text-brand-900 transition-colors hover:text-brand-700"
            aria-label="Go to Karebe home page"
          >
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-brand-100 bg-brand-50 sm:h-16 sm:w-16 lg:h-20 lg:w-20">
              {hasLogo ? (
                <img
                  src={settings.logo_url}
                  alt={settings.logo_alt || settings.business_name}
                  className="h-full w-full object-contain p-1 sm:p-1.5"
                />
              ) : (
                <Store className="h-7 w-7 text-brand-600 sm:h-8 sm:w-8 lg:h-10 lg:w-10" />
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold leading-tight sm:text-xl lg:text-2xl">
                {settings.business_name || 'Karebe Wines & Spirits'}
              </p>
              <p className="hidden text-sm text-brand-500 sm:block">
                Premium wines and spirits
              </p>
            </div>
          </Link>

          <div className="hidden items-center gap-4 md:flex">
            {links.map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'rounded-full px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-brand-100 font-medium text-brand-900'
                      : 'text-brand-600 hover:bg-brand-50 hover:text-brand-900'
                  )}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/cart')} className="relative rounded-full">
              <ShoppingCart className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Cart</span>
              {itemCount > 0 && (
                <span
                  key={itemCount}
                  className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-brand-600 px-1.5 text-xs text-white animate-cart-count"
                  aria-live="polite"
                >
                  {itemCount}
                </span>
              )}
            </Button>

            {user ? (
              <>
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate(getDashboardRoute(user.role))}>
                  <LayoutDashboard className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{user.role === 'rider' ? 'Portal' : 'Dashboard'}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full"
                  onClick={async () => {
                    await logout();
                    navigate('/');
                  }}
                >
                  <LogOut className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </>
            ) : (
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => navigate('/admin/login')}>
                <LogIn className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Login</span>
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3 md:hidden">
          <div className="no-scrollbar -mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1">
            {links.map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'snap-start whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-brand-600 font-medium text-white'
                      : 'border border-brand-200 bg-white text-brand-700'
                  )}
                >
                  {link.label}
                </Link>
              );
            })}
            {user && (
              <Link
                to={getDashboardRoute(user.role)}
                className="snap-start whitespace-nowrap rounded-full border border-brand-200 bg-white px-4 py-2 text-sm text-brand-700"
              >
                {user.role === 'rider' ? 'Portal' : 'Dashboard'}
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default GlobalNav;
